<template>
  <div class="reference-details-section grey lighten-4">
    <vote-history-bar
      v-if="showFullTextVoteHistory"
      :reference="reference"
      full-text-review
    />
    <tabs v-model="selectedTab" class="flex fill-width">
      <tab-item
        v-for="tabInfo in tabsToShow"
        :key="tabInfo.value"
        :value="tabInfo.value"
        :count="tabInfo.count"
        class="flex-grow-1"
      >
        <v-icon class="material-icons-outlined">
          {{ tabInfo.icon }}
        </v-icon>
      </tab-item>
    </tabs>
    <v-divider />
    <tabs-content
      v-model="selectedTab"
      class="data-extraction-tabs-content"
    >
      <tab-content
        v-for="tabInfo in tabsToShow"
        :key="tabInfo.value"
        :value="tabInfo.value"
        class="flex-grow-1 fill-height fill-width"
      >
        <reference-detail-fields
          v-if="tabInfo.value === 'INFO' && hasReference"
          class="fill-height px-6 py-4 grey lighten-4"
          :review="review"
          :reference="reference"
          :show-full-text-review-field="showFullTextReviewField"
          @edit="$emit('edit', $event)"
          @open-tag-settings="openTagSettingsModal"
          @update-tags="$emit('update-tags', $event)"
          @download-full-text="$emit('download-full-text')"
          @delete-full-text="$emit('delete-full-text')"
        >
          <template v-if="allowEdit" #actions>
            <edit-button @click="$emit('edit')" />
          </template>
        </reference-detail-fields>
        <reference-comments-panel
          v-if="tabInfo.value === 'COMMENTS' && hasReference"
          :current-researcher="currentResearcher"
          :reference="reference"
          :is-open="selectedTab === 'COMMENTS'"
          @has-unsaved-comment="
            $emit('update:hasUnsavedComment', $event)
          "
        />
        <data-extraction-controller
          v-if="tabInfo.value === 'DATA' && hasReference"
          :review="review"
          :reference="reference"
          :review-member="currentReviewMember"
          v-bind="extractionPanelProps"
          @submit="$emit('submit')"
        />
        <event-log
          v-if="tabInfo.value === 'EVENTS' && hasReference"
          :reference="reference"
          :event-logs="reference.eventLogs"
          :current-researcher="currentResearcher"
        />
      </tab-content>
    </tabs-content>
    <reference-tag-settings-modal
      :review="review"
      :open="showTagSettingsModal"
      @close="closeTagSettingsModal"
    />
  </div>
</template>

<script>
import EditButton from '@/components/Common/EditButton';
import ReferenceDetailFields from './ReferenceDetailFields';
import { Reference, Researcher, Review } from '@/models';
import Tabs from '@/components/Common/Tabs';
import TabItem from '@/components/Common/TabItem';
import TabsContent from '@/components/Common/TabsContent';
import TabContent from '@/components/Common/TabContent';
import ReferenceCommentsPanel from '@/components/References/ReferenceCommentsPanel';
import DataExtractionController from '@/components/References/DataExtractionController';
import ReferenceTagSettingsModal from '@/components/References/ReferenceTagSettingsModal';
import EventLog from '@/components/EventLog/EventLog';
import VoteHistoryBar from '@/components/References/VoteHistoryBar';

export const SectionTabs = Object.freeze({
  INFO: 'INFO',
  COMMENTS: 'COMMENTS',
  DATA: 'DATA',
  EVENTS: 'EVENTS',
});

export default {
  name: 'ReferenceDetailsSection',
  components: {
    EditButton,
    ReferenceDetailFields,
    Tabs,
    TabItem,
    TabsContent,
    TabContent,
    ReferenceCommentsPanel,
    DataExtractionController,
    ReferenceTagSettingsModal,
    EventLog,
    VoteHistoryBar,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      default: null,
    },
    allowEdit: {
      type: Boolean,
      default: false,
    },
    allowFullTextUpload: {
      type: Boolean,
      default: false,
    },
    allowSubmitForConsensus: {
      type: Boolean,
      default: false,
    },
    disableExtractionPanel: {
      type: Boolean,
      default: false,
    },
    extractionPanelReadonly: {
      type: Boolean,
      default: false,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    tabOrder: {
      type: Array,
      default: () => [
        SectionTabs.INFO,
        SectionTabs.COMMENTS,
        SectionTabs.DATA,
        SectionTabs.EVENTS,
      ],
    },
  },
  emits: [
    'edit',
    'download-full-text',
    'delete-full-text',
    'update:hasUnsavedComment',
    'update-tags',
    'submit',
  ],
  data: () => ({
    selectedTab: SectionTabs.INFO,
    showTagSettingsModal: false,
  }),
  computed: {
    tabsToShow() {
      const SELECTED_TAB_TO_PROPS = {
        [SectionTabs.INFO]: {
          icon: 'info',
        },
        [SectionTabs.COMMENTS]: {
          icon: 'comment',
          count: this.commentsCount,
        },
        [SectionTabs.DATA]: {
          icon: 'list_alt',
        },
        [SectionTabs.EVENTS]: {
          icon: 'history',
        },
      };
      return this.tabOrder.map((tab) => ({
        ...SELECTED_TAB_TO_PROPS[tab],
        value: tab,
      }));
    },
    extractionPanelProps() {
      if (this.allowSubmitForConsensus) {
        return {
          'show-submit': true,
          disabled:
            this.disableExtractionPanel ||
            this.extractionPanelReadonly,
        };
      } else {
        return {
          'collapse-all': true,
        };
      }
    },
    hasReference() {
      return !!this.reference;
    },
    showFullTextVoteHistory() {
      return (
        this.hasReference &&
        (this.reference.fullTextReviewVotes.length > 0 ||
          this.reference.hasFullTextReviewOutcome())
      );
    },
    hasFullTextPdf() {
      return this.hasReference && !!this.reference.fullTextPdfUri;
    },
    commentsCount() {
      return this.reference?.comments?.length || 0;
    },
    showFullTextReviewField() {
      if (!this.allowEdit) {
        return false;
      }
      return this.allowFullTextUpload || this.hasFullTextPdf;
    },
    currentReviewMember() {
      const review = this.review || {};
      const reviewMembers = review.reviewMembers || [];
      return reviewMembers.find(
        (member) => member.researcher.id === this.currentResearcher.id
      );
    },
  },
  methods: {
    openTagSettingsModal() {
      this.showTagSettingsModal = true;
    },
    closeTagSettingsModal() {
      this.showTagSettingsModal = false;
    },
  },
};
</script>

<style>
.data-extraction-tabs-content {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
</style>

<style lang="scss" scoped>
.reference-details-section {
  display: grid;
  grid:
    'tabs' auto
    'divider' auto
    'content' 1fr
    / 100%;
  height: 100%;
}
.reference-details-section-header {
  height: 96px;
}
</style>
